import {
  CATALOGUE_CATEGORY as CC,
  LENGTH_TYPE,
  SYMBOL_REVISION as REV,
  DETECTION_MODE as DTM,
} from '../types';

import {
  DUCK_VERSION_NUMBER as DUCK,
  DEFAULT_LINE_COLOR,
  COMMON_STROKE_WIDTH as CSW,
} from './config';

/** SYMBOL_PALETTE */
export enum PAL {
  /** user confirmed green */
  UCF = '#2ECC71',
  // ======================
  /** Missing any info Duct — Blue */
  MSD = '#4361EE',
  /** Missing any info Pipe — Blue */
  MSP = '#4361ee',
  // ======================
  /** Completed unconfirmed Duct — Pink */
  CUD = '#F18AB4',
  /** Completed unconfirmed Pipe — Pink */
  CUP = '#f18ab4',
  // ===========================
  /** No system — Aqua */
  NSY = '#00CED1',
  /** All found (size + system) — Gold */
  AFD = '#FFD700',
  /** Equipment — orange */
  EQM = '#FC913A',
  /** User added fittings — green */
  UAF = '#2eCC71',
  /** User added lines — green */
  UAL = '#2ecc71',
}

export type SymbolPalette = {
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  /** for fill color */
  transparency: number;
};

type VersionPalette = {
  [key: string]: {
    strokeColor: string;
    fillColor?: string;
    transparency?: number;
  };
};

export const lengthColors: { [key: string]: string } = {
  [LENGTH_TYPE.D]: PAL.UAL, // orange for duct length
  [LENGTH_TYPE.P]: PAL.UAL, // red for pipe length
  [LENGTH_TYPE.F]: PAL.UAL,
  ['DEFAULT']: DEFAULT_LINE_COLOR, // gray
};

export const getDucPalette = () => versionPalette[DUCK];

/**
 * Check palette by version and variant
 *
 * @date 2024/03/19
 * @param version symbol version, '1', '2', or theme color such as `red`, `orange` defined in `SYMBOL_REVISION` ...
 * @param varaint DUCT | PIPE
 * @returns
 */
export const getSymbolPaletteBy = (
  version: string,
  variant?: string,
): SymbolPalette => {
  // CASE 1: legacy way no consideration of variant
  if (!variant) {
    return versionPalette[version] || versionPalette[DUCK];
  }
  // CASE 2: confirmed version is exceptional no need to consider varaint
  if (version === REV.D) return versionPalette[REV.D];

  // CASE 3: palette by version(1, 2) & varaint
  const palette = variantForState[version];
  if (!palette) {
    // FIXME: use themed version to reset symbol stroke color!
    // @2024/05/29
    return { ...versionPalette[DUCK], strokeColor: version };
  }
  // === FINALLY: merge `strokeColor` to original version theme
  const originalPalette = versionPalette[version];
  const variantPalette = palette[variant];
  return { ...originalPalette, ...variantPalette };
};

/**
 * v1 varaint palette, for missing info fittings(DUCT|PIPE)
 */
const variantPaletteV1: VersionPalette = {
  [CC.D]: {
    strokeColor: PAL.MSD,
  },
  [CC.P]: {
    strokeColor: PAL.MSP,
  },
  // for `polygon_area` feature
  [CC.Z]: {
    strokeColor: '#4361EE',
    fillColor: '#00FF00',
    transparency: 0.05,
  },
  [LENGTH_TYPE.D]: {
    strokeColor: PAL.MSD,
  },
  [LENGTH_TYPE.P]: {
    strokeColor: PAL.MSP,
  },
};

/**
 * v2 varaint palette, for completed fittings(DUCT|PIPE)
 */
const variantPaletteV2: VersionPalette = {
  [CC.D]: {
    strokeColor: PAL.CUD, // pink: #F1948A
  },
  [CC.P]: {
    strokeColor: PAL.CUP,
  },
  [LENGTH_TYPE.D]: {
    strokeColor: PAL.CUD,
  },
  [LENGTH_TYPE.P]: {
    strokeColor: PAL.CUP,
  },
};

/**
 * User added dots,lines
 */
const variantPaletteM: VersionPalette = {
  [DTM.M]: {
    strokeColor: PAL.UAF,
  },
};

/**
 * DESIGN RULES:
 * a variant for `DUCT` and `PIPE` after version workflow, v1, v2 applied!
 *
 * @date 2024/03/19
 */
export const variantForState: { [version: string]: VersionPalette } = {
  [REV.B]: variantPaletteV1, // version: 1
  [REV.C]: variantPaletteV2,
  [REV.M]: variantPaletteM,
};

const strokeParency = {
  strokeWidth: 1,
  transparency: 0.002,
};

/**
 * Version could be color name to act as a feature theme
 *
 * @date 2023/11/16
 */
export const versionPalette: { [key: string]: SymbolPalette } = {
  // duck feature version, could be brush color?
  [DUCK]: {
    fillColor: '#000000',
    strokeColor: DEFAULT_LINE_COLOR,
    strokeWidth: 2,
    transparency: 0.002,
  },
  [REV.A]: {
    fillColor: '#ed1941',
    strokeColor: '#aa2116',
    strokeWidth: 2,
    transparency: 1,
  },
  [REV.B]: {
    fillColor: '#000000', // black
    strokeColor: '#0000FF', //  blue
    strokeWidth: CSW,
    transparency: 0.001, // FIXME: remove box shading - 2024/03/04
  },
  [REV.C]: {
    fillColor: '#FCAF17', // orange
    strokeColor: PAL.CUD, // pink for completed symbol
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.D]: {
    fillColor: '#03C04A', // light green
    strokeColor: PAL.UCF, // grey green from simon feedback - @2024/03/06
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.M]: {
    fillColor: '#000000', // black
    strokeColor: '#0000FF', //  blue, will be overrided by variant palette
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.BL]: {
    fillColor: '#041BC5', // blue
    strokeColor: '#041BC5', // blue
    ...strokeParency,
  },
  [REV.G]: {
    fillColor: '#479C4C', // green
    strokeColor: '#479C4C', // green
    ...strokeParency,
  },
  [REV.O]: {
    fillColor: '#ED662C', // orange
    strokeColor: '#ED662C', // orange
    ...strokeParency,
  },
  // === for newly added symbols ===
  [REV.P]: {
    fillColor: '#B241BC', // purple
    strokeColor: '#B241BC', // purple
    ...strokeParency,
  },
  [REV.R]: {
    fillColor: '#D32E39', // red
    strokeColor: '#D32E39', // red
    ...strokeParency,
  },
  [REV.AQ]: {
    fillColor: PAL.NSY, // aqua - no system
    strokeColor: PAL.NSY,
    ...strokeParency,
  },
  [REV.TQ]: {
    fillColor: PAL.AFD, // gold - all found
    strokeColor: PAL.AFD,
    ...strokeParency,
  },
  [REV.Y]: {
    fillColor: '#F9E54D', // yellow
    strokeColor: '#F9E54D', // yellow
    ...strokeParency,
  },
};
